import { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { Download } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { API_ASSET_EXPORT, API_TOTAL_ASSET, ASSET_TYPE_LIST } from "../../../config/Endpoints.js";
import { ownerOptions } from "../../../config/filters.js";
import { getApi } from "../../../utils/api.js";
import { truncateText } from "../../../utils/helpers.js";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";
import { Badge, Button, Select, Spinner } from "../../components/ui/index.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { showError } from "../../utils/toast.ts";

import styles from "./asset-dashboard-page.module.css";

type AssetTypeOption = { assestId: number; name: string };
type AssetTotal = {
  assetTpeId: number;
  assestType: string;
  owner: string;
  assigned: number;
  available: number;
  allocated: number;
  rto: number;
  total: number;
};
type ApiResponse<T> = { data: T };
type PageProps = { title?: string };

const DEFAULT_OWNER = ownerOptions[0].value;
const ALL_ASSET_TYPES = "0";

export default function AssetDashboardPage({ title }: PageProps) {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [assetTypes, setAssetTypes] = useState<AssetTypeOption[]>([]);
  const [assetTypeId, setAssetTypeId] = useState(ALL_ASSET_TYPES);
  const [assets, setAssets] = useState<AssetTotal[]>([]);
  const [isTypesLoading, setIsTypesLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const assetTypeOptions = useMemo(() => [
    { value: ALL_ASSET_TYPES, label: "All" },
    ...assetTypes.map((item) => ({ value: String(item.assestId), label: item.name })),
  ], [assetTypes]);

  useEffect(() => {
    setBreadcrumbs([{ label: title || "Asset Overview" }]);
    return () => setBreadcrumbs([]);
  }, [title, setBreadcrumbs]);

  useEffect(() => {
    let active = true;
    setIsTypesLoading(true);
    getApi(ASSET_TYPE_LIST, Cookies.get("accessToken"))
      .then((response: { data: ApiResponse<AssetTypeOption[]> }) => {
        if (active) setAssetTypes(Array.isArray(response.data?.data) ? response.data.data : []);
      })
      .catch((error: unknown) => {
        console.error("Failed to load asset types:", error);
      })
      .finally(() => {
        if (active) setIsTypesLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    const params = new URLSearchParams({ owner: DEFAULT_OWNER, assestTypeId: assetTypeId });
    getApi(`${API_TOTAL_ASSET}?${params.toString()}`, Cookies.get("accessToken"))
      .then((response: { data: ApiResponse<AssetTotal[]> }) => {
        if (!active) return;
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        setAssets([...rows].sort((a, b) => a.assestType.localeCompare(b.assestType)));
      })
      .catch((error: unknown) => {
        console.error("Failed to load asset totals:", error);
        if (active) setAssets([]);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => { active = false; };
  }, [assetTypeId]);

  const handleExport = async () => {
    setIsExporting(true);
    const params = new URLSearchParams({ owner: DEFAULT_OWNER, assestTypeId: assetTypeId });
    try {
      const response = await fetch(`${API_ASSET_EXPORT}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get("accessToken")}`,
          "Content-Type": "application/vnd.ms-excel",
        },
      });
      if (!response.ok) {
        showError("No Record Available!");
        return;
      }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = "asset_list.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading asset list:", error);
      showError("Unable to export assets. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return <PageShell className={styles.page}>
    <Card className={styles.toolbar}>
      <div className={styles.filter}>
        <Select id="filterAssetType" label="Asset Type" value={assetTypeId} options={assetTypeOptions}
          onChange={setAssetTypeId} disabled={isTypesLoading} />
      </div>
      <Button startIcon={<Download size={17} />} onClick={() => void handleExport()} disabled={isExporting}>
        {isExporting ? "Exporting..." : "Export"}
      </Button>
    </Card>

    <div className={styles.summary} role="status">Asset Types: <strong>{assets.length}</strong></div>

    {isLoading ? <div className={styles.loading}><Spinner label="Loading assets" /></div> : assets.length ? (
      <div className={styles.grid}>
        {assets.map((asset) => (
          <Card key={`${asset.assetTpeId}-${asset.owner}`} padding="none" className={styles.assetCard}>
            <button className={styles.assetAction} type="button"
              onClick={() => navigate(`assetType/${asset.assetTpeId}`, { state: { assetType: asset.assestType } })}>
              <h2 title={asset.assestType}>{truncateText(asset.assestType, 15)}</h2>
              <p className={styles.owner}>{asset.owner}</p>
              <div className={styles.metrics}>
                <Badge variant="neutral">Assigned: {asset.assigned}</Badge>
                <Badge variant="success">Available: {asset.available}</Badge>
                <Badge variant="warning">Allocated: {asset.allocated}</Badge>
                <Badge variant="neutral">RTO: {asset.rto}</Badge>
                <Badge variant="danger">Total: {asset.total}</Badge>
              </div>
            </button>
          </Card>
        ))}
      </div>
    ) : <Card className={styles.empty}><EmptyStateMessage message="No Record Available" /></Card>}
  </PageShell>;
}
